import kriging from './kriging';
import L from 'leaflet';
import {
	VariavelMeteorologica,
	variavelParametros,
} from './intepolacao-parametros';
import { DesvioDTO } from '@modulos/home/submodulos/dados-meteorologicos/interfaces/desvioDTO';

export interface LegendaCor {
	cor: string;
	valorMin: number;
	valorMax: number;
	unidade: string;
	variavel?: string;
	municipio?: string;
	infoAdicional: string;
}

export interface LatLngValue extends ValorPorLocalizacao {
	tipoPCD: string | null;
	municipio: string;
	nomePosto: string;
	valorMin: number;
	valorMax: number;
}

export interface ValorPorNdc {
	nomePosto: string;
	lat: number;
	lng: number;
	precipitacao: number;
	value: number;
}

export interface ValoresBusca {
	municipio: string;
	lat: number;
	lng: number;
	value: number;
	tipoValor: TipoValor;
	unidade: string;
}
export enum TipoValor {
	CLIMATOLOGIA = 'Climatologia',
	Desvio = 'Desvio',
	BALANCO_HIDRICO = 'Balanço hídrico',
}
export interface ValorPorLocalizacao {
	lat: number;
	lng: number;
	value: number;
}

export interface ValorDesvioInterpolacao extends ValorPorLocalizacao {
	desvio: keyof DesvioDTO;
	nome: string;
}

export interface ValorPorMesorregiao extends ValorPorLocalizacao {
	mesorregiao: string;
	nomePosto: string;
	isPrecipitacao: boolean;
}

export interface ValorPorPosto extends ValorPorLocalizacao {
	nomePosto: string;
	isPrecipitacao: boolean;
}
export type ValoresInterpolacao =
	| LatLngValue
	| ValorPorMesorregiao
	| ValorPorLocalizacao
	| ValorDesvioInterpolacao
	| ValorPorNdc;

export interface LatLngDto {
	lat: number;
	lng: number;
	value: number;
}
export interface InterpolacaoCanvasBounds {
	canvas: HTMLCanvasElement;
	latLngBounds: L.LatLngBounds;
	valores: ValoresInterpolacao[];
	legenda: LegendaCor[];
	variograma: any;
	valorConstante?: number;
}

export function gerarLegenda(
	variavel: VariavelMeteorologica,
	intervalos: number[],
	regiao: any
): any[] {
	const parametros = variavelParametros[variavel];

	if (!parametros) {
		console.error(`Parâmetros não encontrados para a variável: ${variavel}`);
		return [];
	}

	const {
		unidade,
		valorMax: paramValorMax,
		valorMin: paramValorMin,
		colormap,
	} = parametros;

	const infoAdicional =
		'infoAdicional' in parametros ? parametros.infoAdicional : [];

	const valorMax = paramValorMax ?? 1200;
	const valorMin = paramValorMin ?? 0;

	const colorMap = colormap;

	if (intervalos) {
		return colorMap.map((value, i) => ({
			cor: value,
			valorMin: intervalos[i],
			valorMax: intervalos[i + 1] - 0.1,
			infoAdicional: infoAdicional[i] ?? '',
			unidade,
			regiao,
		}));
	}

	const range = valorMax - valorMin;
	const step = range / (colorMap.length - 1);

	return colorMap.slice(0, -1).map((value, i) => ({
		cor: value,
		valorMin: valorMin + step * i,
		valorMax: valorMin + step * (i + 1),
		infoAdicional: infoAdicional[i] ?? '',
		unidade,
	}));
}

export function criarCanvasInterpolacao(
	valores: ValoresInterpolacao[],
	variavel: VariavelMeteorologica,
	regiao: number[][][],
	width: number,
	isValoresResumido = false
): InterpolacaoCanvasBounds {
	const params = variavelParametros[variavel];
	if (!params) {
		console.error(`Parâmetros não encontrados para a variável: ${variavel}`);
		return {
			canvas: document.createElement('canvas'),
			latLngBounds: L.latLngBounds([]),
			legenda: [],
			valores: [],
			variograma: {},
		};
	}

	const { sigma2, alpha, model, colormap, valorMin, valorMax, intervalos } =
		params;

	let variograma: any;

	if (isValoresResumido) {
		const listaLatLngDto: LatLngDto[] = valores.map(item => ({
			lng: item.lng,
			lat: item.lat,
			value: item.value,
		}));
		variograma = kriging.train(
			listaLatLngDto.map(value => value.value),
			listaLatLngDto.map(value => value.lng),
			listaLatLngDto.map(value => value.lat),
			model,
			sigma2,
			alpha
		);
	} else {
		variograma = kriging.train(
			valores.map(value => value.value),
			valores.map(value => value.lng),
			valores.map(value => value.lat),
			model,
			sigma2,
			alpha
		);
	}

	if (!variograma || !variograma.M || !variograma.K) {
		console.error('Variograma não gerado corretamente:', variograma);
		return {
			canvas: document.createElement('canvas'),
			latLngBounds: L.latLngBounds([]),
			legenda: [],
			valores: [],
			variograma: {},
		};
	}

	const grid = kriging.grid(regiao, variograma, width);

	const valorMaxAtual = valorMax ?? grid?.zlim?.[1] ?? 1200;
	const valorMinAtual = valorMin ?? grid?.zlim?.[0] ?? 0;

	if (!grid || !grid.data || !Array.isArray(grid.data)) {
		console.error('Grid não gerado corretamente:', grid);
		return {
			canvas: document.createElement('canvas'),
			latLngBounds: L.latLngBounds([]),
			legenda: [],
			valores: [],
			variograma: {},
		};
	}

	// Quando todos os valores de entrada são iguais, o resultado da interpolação por padrão é 0
	// esse trecho de código é para substituir os zeros gerados no grid pelos valores de entrada
	const valorConstante = valores[0]?.value ?? 0; // valor que se repete
	const EPS = 1e-12; //epsilon (tolerância numérica) para comparar floats
	const isTodosValoresIguais = valores.every(
		v => Math.abs(v.value - valorConstante) < EPS
	);

	if (isTodosValoresIguais && grid?.data && Array.isArray(grid.data)) {
		for (let i = 0; i < grid.data.length; i++) {
			const col = grid.data[i];
			for (let j = 0; j < col.length; j++) {
				if (Math.abs(col[j]) < EPS) col[j] = valorConstante; // substitui os valores de grid.data
			}
		}
	}

	const canvas = document.createElement('canvas');

	const widthData = grid.data.length;
	const heightData = Array.isArray(grid.data[0]) ? grid.data[0].length : 0;

	if (widthData === 0 || heightData === 0) {
		console.error('Grid está vazio ou malformado:', grid.data);
		return {
			canvas: document.createElement('canvas'),
			latLngBounds: L.latLngBounds([]),
			legenda: [],
			valores: [],
			variograma: {},
		};
	}

	canvas.width = widthData;
	canvas.height = heightData;

	const intervaloExpandido = kriging.expandirIntervalo(
		valorMaxAtual,
		intervalos,
		1.3,
		2
	);

	const colorMapAtual: string[] = colormap as string[];

	const plot = kriging.plot(
		canvas,
		grid,
		grid.xlim,
		grid.ylim,
		colorMapAtual,
		valorMinAtual,
		valorMaxAtual,
		intervaloExpandido
	);

	return {
		canvas,
		latLngBounds: L.latLngBounds([
			[grid.ylim[0], grid.xlim[0]],
			[grid.ylim[1], grid.xlim[1]],
		]),
		legenda: gerarLegenda(variavel, intervaloExpandido, regiao),
		valores: valores,
		variograma,
		valorConstante: isTodosValoresIguais ? valorConstante : undefined,
	};
}
